import { useState, type FormEvent } from 'react'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { useSearchParams } from 'react-router-dom'
import { auth as authApi, ApiError } from '../api/client'
import { useTranslation } from '../i18n/LanguageContext'
import { useMobile } from '../hooks/useMobile'

interface ResetPasswordPageProps {
  onBack: () => void
}

type State = 'form' | 'saving' | 'success' | 'error'

export default function ResetPasswordPage({ onBack }: ResetPasswordPageProps) {
  const [searchParams]      = useSearchParams()
  const { t }               = useTranslation()
  const isMobile            = useMobile()
  const a                   = t.auth
  const token               = searchParams.get('token') ?? ''

  const [state, setState]   = useState<State>(token ? 'form' : 'error')
  const [password,  setPassword]  = useState('')
  const [confirm,   setConfirm]   = useState('')
  const [fieldError, setFieldError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (password.length < 8) { setFieldError(a.errPasswordMin); return }
    if (password !== confirm) { setFieldError(a.resetMismatch); return }
    setFieldError('')
    setState('saving')
    try {
      await authApi.resetPassword(token, password)
      setState('success')
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setState('error')
      } else {
        setFieldError(a.errGeneric)
        setState('form')
      }
    }
  }

  return (
    <div style={styles.root}>
      <div style={{ ...styles.card, ...(isMobile ? styles.cardMobile : {}) }}>
        {(state === 'form' || state === 'saving') && (
          <>
            <h2 style={styles.title}>{a.resetTitle}</h2>
            <p style={styles.subtitle}>{a.resetSubtitle}</p>
            <form onSubmit={handleSubmit} style={styles.form} noValidate>
              <div style={styles.field}>
                <label style={styles.label}>{a.resetNewPassword}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="auth-input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="········"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={state === 'saving'}
                    style={{ paddingRight: 48 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B4C3B', padding: 4, minHeight: 'unset' }}
                    aria-label={showPassword ? a.hidePassword : a.showPassword}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{a.resetConfirm}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="auth-input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="········"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={state === 'saving'}
                    style={{ paddingRight: 48 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B4C3B', padding: 4, minHeight: 'unset' }}
                    aria-label={showPassword ? a.hidePassword : a.showPassword}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              </div>
              {fieldError && (
                <div role="alert" style={styles.errorBox}>⚠ {fieldError}</div>
              )}
              <button
                type="submit"
                disabled={state === 'saving'}
                style={{ ...styles.btn, opacity: state === 'saving' ? 0.75 : 1 }}
              >
                {state === 'saving' ? a.resetSaving : a.resetBtn}
              </button>
            </form>
          </>
        )}

        {state === 'success' && (
          <>
            <span style={styles.emoji}>✅</span>
            <h2 style={styles.title}>{a.resetSuccess}</h2>
            <button onClick={onBack} style={styles.btn}>{a.backToLogin}</button>
          </>
        )}

        {state === 'error' && (
          <>
            <span style={styles.emoji}>❌</span>
            <h2 style={styles.title}>{a.resetExpired}</h2>
            <button onClick={onBack} style={styles.btn}>{a.backToLogin}</button>
          </>
        )}
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     '#FAF6EF',
    padding:        '24px',
  },
  card: {
    background:    '#FFFFFF',
    borderRadius:  20,
    border:        '2px solid #E8DDD0',
    padding:       '48px 40px',
    maxWidth:      440,
    width:         '100%',
    display:       'flex',
    flexDirection: 'column',
    gap:           16,
    boxShadow:     '0 4px 24px rgba(0,0,0,0.07)',
  },
  cardMobile: { padding: '32px 20px', borderRadius: 0, border: 'none', minHeight: '100vh', justifyContent: 'center' },
  title: {
    fontFamily:  "'Cormorant Garamond', serif",
    fontSize:    '1.8rem',
    fontWeight:  700,
    color:       '#1B4332',
    margin:      0,
    lineHeight:  1.1,
  },
  subtitle: { fontSize: '1rem', color: '#6B4C3B', fontWeight: 500, margin: 0 },
  emoji:    { fontSize: '3rem', lineHeight: 1, textAlign: 'center' as const },
  form:     { display: 'flex', flexDirection: 'column' as const, gap: 20, marginTop: 8 },
  field:    { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  label: {
    fontFamily:    'Nunito, sans-serif',
    fontSize:      '1rem',
    fontWeight:    700,
    color:         '#6B4C3B',
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  errorBox: {
    background:   '#FFF4EE',
    border:       '1.5px solid #F2C8B3',
    borderRadius: 10,
    padding:      '12px 16px',
    fontSize:     '0.95rem',
    fontWeight:   600,
    color:        '#B5541A',
  },
  btn: {
    padding:      '16px',
    fontSize:     '1.05rem',
    fontWeight:   800,
    color:        '#FFFFFF',
    background:   'linear-gradient(135deg, #1B4332, #2D6A4F)',
    border:       'none',
    borderRadius: 12,
    cursor:       'pointer',
    minHeight:    52,
    marginTop:    4,
  },
}
